import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import qs from "qs";
import {
  Badge,
  Button,
  CellText,
  Download01,
  EmptyState,
  FilterBar,
  Gift01,
  PageHeader,
  Pagination,
  SearchLg,
  Table,
  TextInput,
} from "@opendate-app/setlist";
import client from "../../../shared/api/client";
import { toastError } from "../../../shared/toastHelper";
import { formatCents } from "../../GiftCardProducts/giftCardFormat";
import GiftCardTabs, { TAB_CARDS } from "../../GiftCardProducts/components/GiftCardTabs";
import {
  CARD_STATUS_COLORS,
  CARD_STATUS_LABELS,
  SCOPE_LABELS,
  SOURCE_LABELS,
  formatDate,
  searchKind,
} from "../giftCardLabels";
import "../styles/giftCards.css";

const SEARCH_DELAY_MS = 300;
const LAST4_SEARCH_DELAY_MS = 1000;

const optionsFrom = (labels) => Object.entries(labels).map(([value, label]) => ({ value, label }));

const serialize = (params) => qs.stringify(params, { arrayFormat: "brackets" });

const columns = [
  {
    key: "card",
    header: "Card",
    primary: true,
    render: (giftCard) => (
      <a className="gift-cards-link" href={giftCard.path}>
        {`•••• ${giftCard.last4}`}
      </a>
    ),
  },
  {
    key: "owner",
    header: "Owner",
    render: (giftCard) => <CellText primary={giftCard.ownerEmail || "—"} supporting={giftCard.recipientName} />,
  },
  { key: "product", header: "Product", render: (giftCard) => giftCard.productName || SOURCE_LABELS[giftCard.source] },
  {
    key: "initial",
    header: "Starting value",
    align: "right",
    render: (giftCard) => formatCents(giftCard.initialValueCents, giftCard.currency),
  },
  {
    key: "balance",
    header: "Balance",
    align: "right",
    render: (giftCard) => formatCents(giftCard.balanceCents, giftCard.currency),
  },
  {
    key: "status",
    header: "Status",
    render: (giftCard) => (
      <Badge color={CARD_STATUS_COLORS[giftCard.status]} dot size="sm">
        {CARD_STATUS_LABELS[giftCard.status]}
      </Badge>
    ),
  },
  { key: "issued", header: "Issued", render: (giftCard) => formatDate(giftCard.issuedAt) },
];

const GiftCardsList = ({
  featureEnabled,
  searchPath,
  exportPath,
  codeSearchPath,
  issuePath,
  productsPath,
  cardsPath,
  redeemPath,
  products,
  issuedWithinDays,
}) => {
  const [term, setTerm] = useState("");
  const [searchedTerm, setSearchedTerm] = useState("");
  const [filterValues, setFilterValues] = useState({});
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ giftCards: [], totalCount: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lookingUpCode, setLookingUpCode] = useState(false);
  const latestRequest = useRef(0);
  const kind = searchKind(term);
  const codeShaped = kind === "code";

  const searchNow = () => {
    setSearchedTerm(term.trim());
    setPage(1);
  };

  useEffect(() => {
    if (codeShaped) return undefined;
    const timer = setTimeout(searchNow, kind === "last4" ? LAST4_SEARCH_DELAY_MS : SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [term, kind]);

  const query = useMemo(
    () => ({ gift_card_search: searchedTerm, filters: filterValues }),
    [searchedTerm, filterValues]
  );

  useEffect(() => {
    if (!featureEnabled) return;
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    setLoading(true);
    client
      .get(searchPath, { params: { ...query, page }, paramsSerializer: serialize })
      .then(({ data }) => {
        if (requestId !== latestRequest.current) return;
        setResult(data);
        setError(null);
      })
      .catch(() => {
        if (requestId !== latestRequest.current) return;
        setError("Gift cards could not be loaded.");
      })
      .finally(() => {
        if (requestId === latestRequest.current) setLoading(false);
      });
  }, [featureEnabled, searchPath, query, page]);

  const lookUpCode = async () => {
    if (lookingUpCode) return;
    setLookingUpCode(true);
    try {
      const { data } = await client.post(codeSearchPath, { gift_card_code: term.trim() });
      window.location.href = data.path;
    } catch (lookupError) {
      const errors = lookupError?.response?.data?.errors;
      toastError(errors ? errors.join(", ") : "Couldn't look up that code. Please try again.");
      setLookingUpCode(false);
    }
  };

  const filters = useMemo(
    () => [
      { key: "product", label: "Product", options: products },
      { key: "status", label: "Status", options: optionsFrom(CARD_STATUS_LABELS) },
      { key: "scope", label: "Works at", options: optionsFrom(SCOPE_LABELS) },
      { key: "source", label: "Source", options: optionsFrom(SOURCE_LABELS) },
      {
        key: "issued",
        label: "Issued",
        options: issuedWithinDays.map((days) => ({ value: days, label: `Last ${days} days` })),
      },
    ],
    [products, issuedWithinDays]
  );

  const exportCsv = () => {
    window.location.href = `${exportPath}?${serialize(query)}`;
  };

  const header = (
    <PageHeader
      title="Gift cards"
      supportingText="Sell gift cards fans can use on tickets and add-ons."
      actions={
        featureEnabled && (
          <>
            {exportPath && (
              <Button variant="secondary" size="sm" icon={Download01} onClick={exportCsv}>
                Export CSV
              </Button>
            )}
            {issuePath && (
              <Button variant="primary" size="sm" icon={Gift01} onClick={() => (window.location.href = issuePath)}>
                Issue free cards
              </Button>
            )}
          </>
        )
      }
    />
  );

  if (!featureEnabled) {
    return (
      <div className="gift-cards">
        {header}
        <div className="gift-cards-panel gift-cards-empty" data-testid="gift-cards-disabled">
          <EmptyState
            icon={Gift01}
            title="Gift cards aren't on for your team"
            description="Opendate support can turn them on. Your Gift Up cards keep working until you switch."
          />
        </div>
      </div>
    );
  }

  const searching = Boolean(searchedTerm) || Object.values(filterValues).some((values) => values && values.length > 0);
  const resultCount = `${result.totalCount.toLocaleString()} card${result.totalCount === 1 ? "" : "s"}`;

  return (
    <div className="gift-cards">
      {header}
      <GiftCardTabs current={TAB_CARDS} productsPath={productsPath} cardsPath={cardsPath} redeemPath={redeemPath} />
      <div className="gift-cards-panel">
        <div className="gift-cards-toolbar">
          <FilterBar
            variant="plain"
            filters={filters}
            values={filterValues}
            alwaysShow={filters.map((filter) => filter.key)}
            onChange={(nextValues) => {
              setFilterValues(nextValues);
              setPage(1);
            }}
            search={
              <TextInput
                size="sm"
                type="search"
                leadingIcon={SearchLg}
                placeholder="Last 4, email, order number or full code"
                aria-label="Search gift cards"
                value={term}
                onChange={(event) => setTerm(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== "Enter") return;
                  if (codeShaped) {
                    lookUpCode();
                  } else {
                    searchNow();
                  }
                }}
              />
            }
            resultCount={resultCount}
          />
          {codeShaped && (
            <div className="gift-cards-code-hint">
              <span>That looks like a gift card code. Codes are only looked up one at a time.</span>
              <Button variant="link-color" size="sm" onClick={lookUpCode} disabled={lookingUpCode}>
                Look up this code
              </Button>
            </div>
          )}
        </div>
        <Table
          columns={columns}
          data={result.giftCards}
          getRowKey={(giftCard) => giftCard.id}
          loading={loading}
          error={error}
          empty={
            <EmptyState
              icon={Gift01}
              title={searching ? "No cards match" : "No gift cards yet"}
              description={
                searching
                  ? "Try another last 4, email or order number, or clear the filters."
                  : "Cards show up here once fans buy them."
              }
            />
          }
        />
        {result.totalPages > 1 && (
          <div className="gift-cards-pagination">
            <Pagination
              currentPage={page}
              totalPages={result.totalPages}
              onPageChange={(nextPage) => {
                if (!loading) setPage(nextPage);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

GiftCardsList.propTypes = {
  featureEnabled: PropTypes.bool.isRequired,
  searchPath: PropTypes.string.isRequired,
  exportPath: PropTypes.string,
  codeSearchPath: PropTypes.string.isRequired,
  issuePath: PropTypes.string,
  productsPath: PropTypes.string,
  cardsPath: PropTypes.string.isRequired,
  redeemPath: PropTypes.string,
  products: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })).isRequired,
  issuedWithinDays: PropTypes.arrayOf(PropTypes.string).isRequired,
};

GiftCardsList.defaultProps = {
  exportPath: null,
  issuePath: null,
  productsPath: null,
  redeemPath: null,
};

export default GiftCardsList;
